/* eslint-disable react/no-multi-comp */
//types
import type {
    ComponentProps,
    ComponentPropsWithoutRef,
    ComponentPropsWithRef,
    ElementType,
    MutableRefObject
} from 'react';
import { forwardRef, Fragment } from 'react';

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import H2 from '@/components/base/Headings/H2';
import Icon from '@/components/base/Icon';

const SIZE_DEFAULT = 'DEFAULT' as const;
const SIZE_LG = 'lg' as const;
const SIZE_XL = 'xl' as const;
const SIZE_4XL = '4xl' as const;
const SIZE_5XL = '5xl' as const;
const SIZE_6XL = '6xl' as const;
const SIZE_FULL = 'full' as const;
const SIZE_FULL_SCHEDULER = 'full-scheduler' as const;

export const ModalSize = {
    [SIZE_DEFAULT]: 'sm:max-w-[37.5rem] my-8 rounded-2xl',
    [SIZE_LG]: 'sm:max-w-lg my-8 rounded-2xl',
    [SIZE_XL]: 'sm:max-w-2xl my-8 rounded-2xl',
    [SIZE_4XL]: 'sm:max-w-4xl my-8 rounded-2xl',
    [SIZE_5XL]: 'sm:max-w-[1000px] rounded-2xl',
    [SIZE_6XL]:
        'w-[calc(100vw-80px)] max-w-[1600px] max-h-[calc(100vh-80px)] my-10 mx-auto rounded-2xl',

    [SIZE_FULL_SCHEDULER]: 'w-screen flex-1 border border-white dark:border-icoDarkMode-anthracite',
    [SIZE_FULL]: 'flex-1 m-10 rounded-2xl'
} as const;

export const MODAL_DIALOG_CLASSNAME = 'modal-dialog';

export type ModalProps = Partial<ComponentProps<typeof Dialog>> & {
    children: React.ReactNode;
    size?: keyof typeof ModalSize;
    open: boolean;
    onDismiss?: () => void;
    withCloseButton?: boolean;
    overlayClosable?: boolean;
    scrollInside?: boolean;
    wrapperWidthFull?: boolean;
    scrollInsideWrapperClass?: string;
    initialFocus?: MutableRefObject<HTMLElement>;
    contentWrapperRef?: MutableRefObject<HTMLDivElement>; //Possibility to pass an external ref to the content to bind events like scroll to this context
};

const Modal = forwardRef<HTMLDivElement, ModalProps>(
    (
        {
            children,
            size = SIZE_DEFAULT,
            open,
            onDismiss,
            withCloseButton = true,
            overlayClosable = true,
            scrollInside = false,
            wrapperWidthFull = true,
            scrollInsideWrapperClass,
            className,
            contentWrapperRef,
            ...restProps
        },
        ref
    ) => {
        const { t } = useTranslation('commons');
        return (
            <Transition show={open} as={Fragment}>
                {/* You must have one focusable element inside your modal or
                you should pass an initialFocus prop and add a ref to an element
                onto an element*/}
                <Dialog
                    as="div"
                    ref={ref}
                    className={classNames(
                        'fixed inset-0 overflow-y-auto overflow-x-hidden',
                        {
                            'z-30': size !== SIZE_FULL && size !== SIZE_FULL_SCHEDULER,
                            'z-50': size === SIZE_FULL || size === SIZE_FULL_SCHEDULER
                        },
                        MODAL_DIALOG_CLASSNAME,
                        className
                    )}
                    static
                    open={open}
                    onClose={() => {
                        if (overlayClosable && typeof onDismiss === 'function') {
                            onDismiss();
                        }
                    }}
                    {...restProps}>
                    <div
                        className={classNames('text-center', {
                            'min-h-screen px-3':
                                size !== SIZE_FULL &&
                                size !== SIZE_FULL_SCHEDULER &&
                                size !== SIZE_6XL,
                            'flex min-h-screen': size === SIZE_FULL || size === SIZE_FULL_SCHEDULER
                        })}>
                        {size !== 'full-scheduler' ? (
                            <TransitionChild
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    className={classNames('fixed inset-0 bg-icoBlack/90')}
                                />
                            </TransitionChild>
                        ) : null}

                        {/* This element is to trick the browser into centering the modal contents. */}
                        <span className="inline-block h-screen align-middle" aria-hidden="true">
                            &#8203;
                        </span>
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="scale-95 opacity-0"
                            enterTo="scale-100 opacity-100"
                            leave="ease-in duration-300"
                            leaveFrom="scale-100 opacity-100"
                            leaveTo="scale-95 opacity-0">
                            <DialogPanel
                                className={classNames(
                                    'relative inline-block bg-white text-left align-middle transition-all dark:bg-icoDarkMode-anthracite',
                                    ModalSize[size],
                                    {
                                        'w-full': wrapperWidthFull,
                                        'max-h-[calc(100vh-80px)]': size === SIZE_FULL
                                    }
                                )}>
                                {size === SIZE_FULL ? (
                                    <div className="h-full overflow-hidden rounded-2xl">
                                        <div className="scrollbar h-full" ref={contentWrapperRef}>
                                            <div className="flex h-min min-h-full w-full">
                                                {children}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {scrollInside ? (
                                    <div className={scrollInsideWrapperClass}>{children}</div>
                                ) : null}
                                {size !== SIZE_FULL && !scrollInside ? <>{children}</> : null}
                                {/* Absolutely position close button👇 */}
                                {withCloseButton ? (
                                    <button
                                        type="button"
                                        aria-label={t('close')}
                                        className={classNames(
                                            'absolute inline-flex items-center justify-center rounded-full',
                                            'h-5 w-5',
                                            {
                                                'bg-icoGray-400 shadow-icoXL backdrop-blur-md firefox:bg-gradient-to-tr firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:bg-icoDarkMode-wolf/20 dark:firefox:bg-icoDarkMode-deepBlack dark:firefox:bg-opacity-90 dark:firefox:bg-none':
                                                    size !== SIZE_5XL && size !== SIZE_6XL,
                                                'bg-icoGray-400 dark:bg-icoDarkMode-wolf/20':
                                                    size === SIZE_5XL || size === SIZE_6XL,
                                                'right-4 top-4 sm:right-5 sm:top-5':
                                                    size !== SIZE_FULL,
                                                'right-3 top-3 sm:right-5 sm:top-5':
                                                    size === SIZE_FULL
                                            }
                                        )}
                                        onClick={onDismiss}>
                                        <Icon icon="close" className="text-sm text-white" />
                                    </button>
                                ) : null}
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>
        );
    }
);

Modal.displayName = 'Modal';

// Modal Body 👇
const DefaultBodyStyle = 'px-5 sm:px-15 pt-15';

type BodyProps = ComponentPropsWithRef<'div'>;
const Body = forwardRef<HTMLDivElement, BodyProps>(({ className, ...restProps }, ref) => (
    <div className={className ?? DefaultBodyStyle} ref={ref} {...restProps} />
));

Body.displayName = 'Modal.Body';

//Modal Title 👇
const DefaultTitleStyle = 'text-center';

type TitleProps = ComponentPropsWithoutRef<'h2'>;
const Title = ({ className, ...restProps }: TitleProps) => (
    <Dialog.Title as={H2} className={classNames(DefaultTitleStyle, className)} {...restProps} />
);

// Modal Content 👇
const DefaultContentStyle = 'py-m';

export type ContentProps = ComponentPropsWithoutRef<'div'>;
const Content = ({ className, ...restProps }: ContentProps) => (
    <Dialog.Description as="div" className={className ?? DefaultContentStyle} {...restProps} />
);

// Modal Footer 👇
const DefaultFooterStyle = 'flex items-center justify-between px-5 sm:px-15 pb-12';
type FooterProps = ComponentPropsWithoutRef<'div'> & {
    as?: ElementType;
    isRow?: boolean;
    divider?: boolean;
};
const Footer = ({
    className,
    as: Component = 'div',
    isRow = true,
    divider = false,
    ...restProps
}: FooterProps) => {
    return (
        <>
            {divider ? (
                <hr className="border-t-1 mt-12 border-icoGray-300 dark:border-icoDarkMode-beton" />
            ) : null}
            <Component
                className={
                    className ??
                    classNames(DefaultFooterStyle, {
                        'flex-col space-y-m': !isRow,
                        'pt-m': divider,
                        'pt-8': !divider
                    })
                }
                {...restProps}
            />
        </>
    );
};

const ModalCompound = Object.assign({}, Modal, {
    Body,
    Title,
    Content,
    Footer
});

export default ModalCompound;
